import { LevelSim, SimConstants, Tower } from "@highmarch/domain";
import { Settings } from "../../ui/settings";
import { hqTowerName, lib } from "./common";
import { TowerFeel } from "./towerFeel";

/**
 * The fortress's strike rhythm. The sim fires the fortress every `fireIntervalTicks + 1` ticks — 0.65 s at ×1, a
 * fifth of a second at ×3 — and art/DEV_HANDOFF.md §3 chains a 1.5 s `charge` → `fire` → `idle` around each strike.
 * Both cannot hold for every shot. The old view started `charge` 30 ticks before a shot, which with a 12-tick
 * interval was always: the charge ran once, `fire` was swallowed while it played (and at ×2/×3 the shot came before
 * the charge had half run), and every shot fired the whole hero strike — sky bolt, pillar, hit-stop, screen flash —
 * two to five times a second, so the bolts stole each other's pooled meshes mid-flight.
 *
 * Now the view picks the moments: the strike (fire clip, full VFX and beats) lands on the shot a charge was planned
 * to end on; the shots in between are zaps (one bolt, vfx.js BASE_ZAP). After a strike's `fire`, the next charge runs
 * to the first shot at least `MinChargeSeconds` away (the sim's cooldown says when), fitting the clip to that length,
 * so it ends on its shot at any game speed; while the first enemy is still approaching (within `ApproachCells` beyond
 * the range) it runs `ChargeSeconds`, and the shots that come before it has run are zaps. A shot up to `StrikeSlack`
 * s early (frame and tick rounding) still counts; the clip runs `Overlap` s past its planned end so `fire` fades in
 * over the charged pose. A `fire` never starts over a `charge` that still has a while to run: the rig would blend the
 * two until the charge ended (a replaced one-shot plays on), leaving the arc stubs out after the strike. The conductor's glow rises to `ChargedGlow` over a charge and holds there while a charged
 * fortress has an enemy near (each flash lasts `GlowHoldSeconds`, renewed every frame).
 */
export const FortressRhythm = {
  ChargeSeconds: 1.5,
  MinChargeSeconds: 1.1,
  StrikeSlack: 0.1,
  Overlap: 0.05,
  ApproachCells: 1.5,
  ChargedGlow: 4,
  GlowHoldSeconds: 0.15,
} as const;

/**
 * Seconds until the fortress shot a charge should end on: the next shot is `ticksToNext` ticks away and they follow
 * every `periodTicks`; the first one at least `min` s away (at `secondsPerTick`).
 */
export function strikeLead(ticksToNext: number, periodTicks: number, secondsPerTick: number, min: number = FortressRhythm.MinChargeSeconds): number {
  const period = Math.max(1, periodTicks);
  const ticks = Math.max(1, ticksToNext);
  const behind = min / secondsPerTick - ticks;              // ticks short of the minimum
  return (ticks + (behind > 0 ? Math.ceil(behind / period) * period : 0)) * secondsPerTick;
}

/**
 * The rate a tower's `fire` clip plays at (TowerFeel.fireRate): its length against the real seconds between shots —
 * the sim's interval (gear fire-rate included) at the game speed the player chose. The projectile's release delay
 * (vfxRouter) and the shot's sound (effects.ts) divide by the same rate, so the round still leaves on the clip's
 * release frame.
 */
export function towerFireRate(sim: LevelSim, t: Tower): number {
  const a = sim.balance.towers[t.archetype];
  if (a.isBase) return 1;
  const fire = lib()?.clipsOf(`hq:${hqTowerName(a.id, false, t.tier)}`).find((c) => c.name === "fire");
  const speed = Math.max(1, Settings.current.speed);
  return TowerFeel.fireRate(fire?.duration ?? 0, sim.fireIntervalOf(t) / SimConstants.TicksPerSecond / speed);
}
